import { useCallback, useMemo } from "react";
import { QR_LIGHT, qrSvgDocument, qrSymbol, rasterEdgePx } from "../../lib/qrSymbol";
import { Button } from "./Button";
import { useCopyAttempt } from "./Copy";

/**
 * A QR code, drawn as one SVG path, and a button that puts the same code on the
 * clipboard as a picture.
 *
 * ## Why SVG on screen
 *
 * The end of this thing's life is a printed sticker on a charger in the sun. A
 * raster QR is resampled by whatever prints it and the modules blur into each
 * other; vector geometry survives the browser's print path, a screenshot, and being
 * scaled to whatever the label stock is. So *Print* remains the way these reach
 * label stock, and the clipboard image is for the other half of the job — pasting a
 * code into a work order, a chat message to a site engineer, a label template that
 * will not take an SVG.
 *
 * ## Why the modules are a path and not 400 rects
 *
 * A station URL fits a version-3 code: 29 modules square, about half of them dark.
 * `lib/qrPath` merges horizontal runs into single path commands, which is a few
 * dozen commands instead of several hundred elements — and a DOM the browser can
 * print without thinking about it. It lives there, and the encode lives in
 * `lib/qrSymbol`, because those are the parts that can be wrong invisibly and they
 * have tests.
 *
 * ## No `dangerouslySetInnerHTML`
 *
 * The library will emit finished SVG markup, and this deliberately takes the raw
 * module bitmap instead and builds the element in React. The value encoded here is
 * a URL assembled from a station id; injecting library-formatted markup into the
 * document for the sake of skipping twenty lines of geometry is not a trade worth
 * making in a panel that also renders credentials.
 */

/**
 * The design tokens, **with literal fallbacks**, and the fallbacks are the whole
 * reason these are constants rather than inline strings.
 *
 * An unresolvable `var()` on `fill` is invalid at computed-value time, which paints
 * black — so a bare `var(--color-ink)` gives a *solid black square* the moment the
 * element is separated from this document's stylesheet: serialised to a data URL,
 * dragged to the desktop, pasted into a document, or rasterised by a tool. That is
 * not a theoretical path for a graphic whose entire purpose is to be extracted and
 * printed, and it fails silently — a black square looks like a rendering bug, not
 * like a colour that did not resolve.
 *
 * In this document the tokens win and the code matches every other surface. Outside
 * it, the fallbacks are pure black on pure white. (The copied image does not rely on
 * these at all: it is built from `lib/qrSymbol`'s literal colours, because a file on
 * the clipboard is *always* outside this document.)
 */
const DARK = "var(--color-ink, #000000)";
const LIGHT = "var(--color-paper-raised, #ffffff)";

export interface QrCodeProps {
  /** Encoded verbatim. */
  readonly value: string;
  /**
   * What the code is, for a screen reader — "Connector 2", not the URL. The URL is
   * rendered as selectable text beside the code by every caller, which is what a
   * non-sighted operator actually needs; reading 40 characters of percent-encoded
   * address aloud is not.
   */
  readonly label: string;
  /** Rendered edge length in px, quiet zone included. */
  readonly size?: number;
  readonly className?: string;
}

export function QrCode({ value, label, size = 152, className = "" }: QrCodeProps) {
  const symbol = useMemo(() => qrSymbol(value), [value]);

  /**
   * Nothing rather than a broken square. The only way this fails is a value past
   * the largest symbol's capacity, which a station URL is not — but a caller that
   * finds a way there gets the URL as text (which every caller renders) instead of
   * an unscannable graphic that looks like it works.
   */
  if (symbol === undefined) return null;

  return (
    <svg
      role="img"
      aria-label={label}
      viewBox={`0 0 ${symbol.modules} ${symbol.modules}`}
      width={size}
      height={size}
      // Modules are whole units in this viewBox, so snapping to the device pixel
      // grid is what keeps them from being drawn as grey blur at small sizes.
      shapeRendering="crispEdges"
      className={className}
    >
      {/* The quiet zone is part of the symbol, so it is painted, not left transparent. */}
      <rect width={symbol.modules} height={symbol.modules} fill={LIGHT} />
      <path d={symbol.path} fill={DARK} />
    </svg>
  );
}

/**
 * Whether this browser can be handed a picture at all.
 *
 * `ClipboardItem` is absent outside a secure context — which a panel reached over
 * plain http on a depot LAN is — and image writes are the part of the clipboard API
 * that arrived last. The button is not rendered when it cannot work: unlike a text
 * copy, there is no "select it by hand" fallback for a graphic, so a control that
 * silently does nothing on every press is worse than one that was never there.
 * Print still produces the code on those browsers.
 */
const CAN_COPY_IMAGES =
  typeof ClipboardItem === "function" && typeof navigator?.clipboard?.write === "function";

export interface CopyQrImageProps {
  /** The same value the visible code was drawn from. */
  readonly value: string;
  /** What is being copied, for the accessible name — "Connector 2 code". */
  readonly what: string;
}

/**
 * Puts the code on the clipboard as a PNG.
 *
 * PNG rather than SVG because that is what a clipboard paste target understands:
 * the image flavours browsers agree to write are effectively `image/png` alone, and
 * a chat window or a document handed anything else pastes nothing.
 */
export function CopyQrImage({ value, what }: CopyQrImageProps) {
  const write = useCallback(
    () =>
      /*
        The *promise* is handed to `ClipboardItem`, not an awaited blob. Safari ties
        a clipboard write to the gesture that started it and rejects one that resumes
        after an await; passing the pending blob keeps the write in the click's own
        turn while the rasterising happens behind it. Chrome accepts the same form.
      */
      navigator.clipboard.write([new ClipboardItem({ "image/png": pngOf(value) })]),
    [value],
  );
  const { isCopied, attempt } = useCopyAttempt(write);

  /*
    A failed attempt says nothing, as everywhere else: the code is on screen, its
    address is selectable beside it, and Print still produces it. An error line here
    would be a second thing to style for a case whose remedy is already on the page.
  */
  if (!CAN_COPY_IMAGES) return null;

  return (
    // Bordered rather than ghost: this sits under a caption in the same micro
    // uppercase, and a borderless control there reads as a second label rather than
    // as something to press.
    <Button variant="secondary" aria-label={`${what}: copy as an image`} onClick={attempt}>
      {isCopied ? "Copied" : "Copy image"}
    </Button>
  );
}

/**
 * The code, rasterised to a PNG blob.
 *
 * A canvas rather than the rendered element: the symbol is re-encoded from the same
 * value through the same `lib/qrSymbol` the visible code came from, into a document
 * carrying literal colours and a whole-multiple pixel size. Serialising the on-screen
 * SVG instead would carry this panel's `var()` colours out of the panel, and paint a
 * black square.
 */
async function pngOf(value: string): Promise<Blob> {
  const symbol = qrSymbol(value);
  if (symbol === undefined) throw new Error("value cannot be encoded as a QR code");

  const edge = rasterEdgePx(symbol.modules);
  const image = new Image(edge, edge);
  // `encodeURIComponent` rather than `btoa`: the markup is ASCII today, and a base64
  // step that throws on the first non-Latin-1 character is a trap for whoever edits
  // the document next.
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(qrSvgDocument(symbol, edge))}`;
  await image.decode();

  const canvas = document.createElement("canvas");
  canvas.width = edge;
  canvas.height = edge;

  const context = canvas.getContext("2d");
  if (context === null) throw new Error("no 2d canvas context");

  // A canvas starts transparent, and a transparent PNG pasted onto a dark slide is
  // an unscannable code. The quiet zone has to be actual white.
  context.fillStyle = QR_LIGHT;
  context.fillRect(0, 0, edge, edge);
  context.drawImage(image, 0, 0, edge, edge);

  return await new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => (blob === null ? reject(new Error("canvas produced no image")) : resolve(blob)),
      "image/png",
    );
  });
}
